import React from 'react';
import { IconButton } from '@lumx/react';
import MdiIcon from '@mdi/react';
import { mdiAccountMultipleOutline, mdiApplicationOutline, mdiClose } from '@mdi/js';
import { MobileConfig, NodeType, OUNode, Site, TreeNode } from '../../types';
import OUTreeRow from './OUTreeRow';
import AddUnitForm from './AddUnitForm';

interface Props {
  nodes: TreeNode[];
  depth: number;
  expandedIds: Set<string>;
  addingChildOf: string | null;
  mobileConfigs: MobileConfig[];
  allNodes: OUNode[];
  allSites: Site[];
  sitesExpandedIds: Set<string>;
  onSitesToggle: (id: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onConvertType: (id: string) => void;
  onAddChild: (parentId: string | null) => void;
  onDeleteRequest: (id: string) => void;
  onAddChildSave: (name: string, type: NodeType, parentId: string | null) => void;
  onAddChildCancel: () => void;
  onMobileConfigChange: (nodeId: string, configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => void;
  onViewUsers: (id: string) => void;
  onViewSiteGroups: (siteId: string, siteName: string) => void;
  onDragStart: (e: React.DragEvent, kind: 'ou', id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, targetNodeId: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  dragOverId: string | null;
  draggingId: string | null;
  dragInsert: { targetId: string; position: 'before' | 'after' } | null;
  showSitesColumn?: boolean;
  readOnlyAssignments?: boolean;
  readOnlyStructure?: boolean;
  onUnassign?: (siteId: string) => void;
}

const TreeRenderer: React.FC<Props> = (props) => {
  const { nodes, depth, expandedIds, addingChildOf, allSites, sitesExpandedIds, onSitesToggle } = props;

  return (
    <>
      {nodes.map(node => {
        const expanded = expandedIds.has(node.id);
        const hasChildren = node.children.length > 0;
        const assignedSites = allSites.filter(s => s.assignedNodeId === node.id);
        const siteCount = assignedSites.length;
        const sitesExpanded = sitesExpandedIds.has(node.id);

        return (
          <React.Fragment key={node.id}>
            <OUTreeRow
              node={node}
              depth={depth}
              expanded={expanded}
              mobileConfigs={props.mobileConfigs}
              allNodes={props.allNodes}
              siteCount={siteCount}
              siteNames={assignedSites.map(s => s.name)}
              sitesExpanded={sitesExpanded}
              onSitesToggle={onSitesToggle}
              onToggle={props.onToggle}
              onRename={props.onRename}
              onConvertType={props.onConvertType}
              onAddChild={props.onAddChild}
              onDeleteRequest={props.onDeleteRequest}
              onMobileConfigChange={props.onMobileConfigChange}
              onViewUsers={props.onViewUsers}
              onDragStart={props.onDragStart}
              onDragOver={props.onDragOver}
              onDrop={props.onDrop}
              onDragLeave={props.onDragLeave}
              dragOverId={props.dragOverId}
              draggingId={props.draggingId}
              dragInsert={props.dragInsert}
              showSitesColumn={props.showSitesColumn}
              readOnlyAssignments={props.readOnlyAssignments}
              readOnlyStructure={props.readOnlyStructure}
            />
            {sitesExpanded && siteCount > 0 && (
              <div className="site-rows-box">
                {assignedSites.map(site => (
                  <div
                    key={site.id}
                    className="site-sub-row"
                    style={{ paddingLeft: `${60 + (depth - 1) * 20}px` }}
                  >
                    <span className="site-sub-row__icon">
                      <MdiIcon path={mdiApplicationOutline} size={0.65} color="var(--color-dark-l1)" />
                    </span>
                    <span className="site-sub-row__name">{site.name}</span>
                    <IconButton
                      label="View site groups"
                      icon={mdiAccountMultipleOutline}
                      emphasis="low"
                      size="s"
                      onClick={e => { (e as React.MouseEvent).stopPropagation(); props.onViewSiteGroups(site.id, site.name); }}
                    />
                    {props.onUnassign && (
                      <button
                        className="site-sub-row__remove"
                        onClick={() => props.onUnassign!(site.id)}
                        title="Remove"
                      >
                        <MdiIcon path={mdiClose} size={0.6} />
                        <span>Remove</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {expanded && (hasChildren || addingChildOf === node.id) && (
              <div className={`children-box depth-${Math.min(depth, 4)}`}>
                {hasChildren && (
                  <TreeRenderer
                    {...props}
                    nodes={node.children}
                    depth={depth + 1}
                  />
                )}
                {addingChildOf === node.id && (
                  <AddUnitForm
                    parentId={node.id}
                    nodes={props.allNodes}
                    onSave={props.onAddChildSave}
                    onCancel={props.onAddChildCancel}
                  />
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default TreeRenderer;
